import { cn } from '@invent/wl-ui-kit-next';
import { Button, Label } from '@invent/wl-ui-kit-next/ui';
import { ImageOff } from 'lucide-react';
import * as React from 'react';

import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger
} from '@/design';
import { mediaApi } from './api/media-api';
import type { ContentImageKind, IContentImage, IContentImageRef } from './api/media-contracts';

/**
 * The media library, once per screen rather than once per control (WEB-09).
 *
 * **Why a module-level cache and not a provider.** Three different screens place images — the
 * post editor has two pickers on it, the team editor one per open row, the site pages one per
 * image block — and each needs the whole library to draw the picture it is holding and to offer
 * the rest. A provider would have to be mounted above all of them, which is `ContentArea`, and
 * the area already carries the mock store; a second context there for a list that never changes
 * between screens is ceremony. One cached promise, shared by every consumer, is the same
 * behaviour in thirty lines.
 *
 * **Every write invalidates it.** `refreshLibrary()` drops the cache and tells the mounted
 * consumers to read again, so an upload on the Media screen is in the post editor's picker
 * without a reload. A cache nobody busts is how a picker ends up offering an image that was
 * replaced ten minutes ago.
 */

let pending: Nullable<Promise<IContentImage[]>> = null;
const listeners = new Set<() => void>();

function load(): Promise<IContentImage[]> {
  pending ??= mediaApi.list().catch((error: unknown) => {
    // Cleared on failure, so the next mount retries rather than every consumer holding one
    // rejected promise for the life of the tab.
    pending = null;
    throw error;
  });
  return pending;
}

/** Drops the cached library and asks every mounted consumer to read it again. */
export function refreshLibrary(): void {
  pending = null;
  for (const listener of listeners) listener();
}

export interface ILibrary {
  images: IContentImage[];
  loading: boolean;
  error: Nullable<string>;
}

/** The library as a screen reads it. Shared: the second caller does not fetch. */
export function useLibrary(): ILibrary {
  const [state, setState] = React.useState<ILibrary>({
    images: [],
    loading: true,
    error: null
  });

  const read = React.useCallback(() => {
    let live = true;
    setState((current) => ({ ...current, loading: true }));
    load().then(
      (images) => live && setState({ images, loading: false, error: null }),
      (error: unknown) =>
        live &&
        setState({
          images: [],
          loading: false,
          error: error instanceof Error ? error.message : 'The media library could not be read.'
        })
    );
    return () => {
      live = false;
    };
  }, []);

  React.useEffect(() => {
    let cancel = read();
    const listener = () => {
      cancel();
      cancel = read();
    };
    listeners.add(listener);
    return () => {
      listeners.delete(listener);
      cancel();
    };
  }, [read]);

  return state;
}

/** What the library offers to place: everything a replacement has not fully superseded. */
export function placeable(images: IContentImage[], kinds?: ContentImageKind[]): IContentImage[] {
  return images.filter(
    (image) => image.replacedByImageId === null && (!kinds || kinds.includes(image.kind))
  );
}

/**
 * A picture, or the fact that there is not one.
 *
 * `loading="lazy"` on every tile: a library screen is a grid of full-size images from blob
 * storage, and the browser is better placed than this component to decide which of them are worth
 * fetching.
 */
export function Thumb({
  image,
  size = 'md',
  className
}: {
  image: Nullable<IContentImageRef | IContentImage>;
  size?: 'sm' | 'md' | 'lg';
  className?: string;
}) {
  const box = { sm: 'h-10 w-10', md: 'h-16 w-16', lg: 'aspect-[4/3] w-full' }[size];
  const classes = cn('overflow-hidden rounded-md border border-border bg-muted', box, className);

  if (!image) {
    return (
      <div className={cn(classes, 'grid place-items-center text-muted-foreground')}>
        <ImageOff className="h-4 w-4" aria-hidden />
        <span className="sr-only">No image</span>
      </div>
    );
  }
  return (
    <img
      src={image.url}
      alt={image.alt}
      loading="lazy"
      className={cn(classes, 'object-cover')}
      width={image.width}
      height={image.height}
    />
  );
}

/**
 * The crop, loaded when somebody first asks for one. Lazily, too, because `crop-dialog` reads
 * this module's `FileName` and `refreshLibrary`, and a static import back would make the two a
 * cycle.
 */
const CropDialog = React.lazy(() =>
  import('./crop-dialog').then((module) => ({ default: module.CropDialog }))
);

/**
 * One image offered for placing, and "Crop…" beside it (WEB-09 AC3): the cut is a new library
 * item, so the original stays as it was, and it is the cut that gets placed.
 */
function OfferedImage({
  image,
  chosen = false,
  sized = false,
  onPick,
  onCrop
}: {
  image: IContentImage;
  chosen?: boolean;
  /** Says the picture's size under its name, where the slot has a shape to fill. */
  sized?: boolean;
  onPick: () => void;
  onCrop: () => void;
}) {
  return (
    <div
      className={cn(
        'grid gap-1 rounded-md border p-2 transition hover:border-[var(--color-sapphire)]',
        chosen ? 'border-[var(--color-sapphire)]' : 'border-border'
      )}
    >
      <button type="button" onClick={onPick} className="grid gap-1 text-left">
        <Thumb image={image} size="lg" />
        <span className="truncate text-xs" title={image.alt || image.fileName}>
          {image.alt || image.fileName}
        </span>
        {sized && (
          <span className="text-[0.6875rem] text-muted-foreground">
            {image.width}×{image.height}
          </span>
        )}
      </button>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        className="justify-self-start"
        onClick={onCrop}
      >
        Crop…
      </Button>
    </div>
  );
}

/**
 * Choosing a library image from somewhere that cannot hold a dialog of its own — the rich-text
 * toolbar, which lives in the design system and may not reach into this domain (WEB-06 AC3).
 *
 * It hands back a promise and a node: the caller awaits the promise when its button is pressed,
 * and renders the node wherever it has room. Closing the dialog resolves null rather than leaving
 * the promise hanging, so a cancel is an answer and the editor's chain simply does nothing.
 */
export function useImageChooser(): {
  choose: () => Promise<Nullable<IContentImage>>;
  chooser: React.ReactNode;
} {
  const { images, loading } = useLibrary();
  const [pending, setPending] = React.useState<Nullable<(image: Nullable<IContentImage>) => void>>(
    null
  );

  const choose = React.useCallback(
    () => new Promise<Nullable<IContentImage>>((resolve) => setPending(() => resolve)),
    []
  );

  // The image being cut; the chooser steps aside for the crop and comes back if it is cancelled,
  // with the promise still waiting on a choice.
  const [cropping, setCropping] = React.useState<Nullable<IContentImage>>(null);

  const answer = (image: Nullable<IContentImage>) => {
    pending?.(image);
    setPending(null);
  };

  const offered = placeable(images);

  return {
    choose,
    chooser: cropping ? (
      <React.Suspense fallback={null}>
        <CropDialog
          image={cropping}
          onDone={(cropped) => {
            setCropping(null);
            if (cropped) answer(cropped);
          }}
        />
      </React.Suspense>
    ) : pending ? (
      <Dialog open onOpenChange={(open) => !open && answer(null)}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>Place an image</DialogTitle>
            <DialogDescription>
              From the media library. It is placed where the cursor is; Crop… places a cut of it
              and keeps the original. Uploading is on the Media screen.
            </DialogDescription>
          </DialogHeader>
          <DialogBody className="max-h-[55dvh] grid-cols-[repeat(auto-fill,minmax(9rem,1fr))] gap-3 overflow-y-auto">
            {offered.map((image) => (
              <OfferedImage
                key={image.imageId}
                image={image}
                onPick={() => answer(image)}
                onCrop={() => setCropping(image)}
              />
            ))}
            {offered.length === 0 && (
              <p className="text-sm text-muted-foreground">
                {loading
                  ? 'Reading the library…'
                  : 'The library is empty — upload an image on the Media screen first.'}
              </p>
            )}
          </DialogBody>
        </DialogContent>
      </Dialog>
    ) : null
  };
}

/**
 * A file name that cannot push a dialog wider than the screen.
 *
 * An uploaded name is whatever the editor's machine called the file, and a browser's download
 * folder produces names of a hundred characters with no space in them to wrap at. Dropped into a
 * heading as plain text, one of those stretches the dialog past the viewport and pushes its close
 * button off-screen — so the name is clipped here and carried in full by `title`, which is where
 * somebody looks when a name is cut.
 *
 * It is `min-w-0` because a flex child will not shrink below its content otherwise, and
 * `truncate` on a box that never shrinks does nothing at all.
 */
export function FileName({ name, className }: { name: string; className?: string }) {
  return (
    <span className={cn('min-w-0 truncate', className)} title={name}>
      {name}
    </span>
  );
}

/**
 * Choosing a library image for a slot — a post's lead image, a sharing card, a headshot.
 *
 * It takes and gives back an **id**, because that is what every record stores: a placement is a
 * foreign key into the library, which is what lets a replacement move every placement at once
 * (WEB-09 AC2). The picture to draw is looked up in the shared library rather than passed in, so
 * a caller holding an id out of a saved record needs nothing else to show it.
 */
export function ImagePicker({
  label,
  value,
  current,
  onPick,
  kinds,
  disabled,
  describedBy
}: {
  label: string;
  value: Nullable<string>;
  /**
   * The picture the record itself answered with, where it has one.
   *
   * **The library is for offering alternatives, not for knowing what is already placed.** The
   * cache below is module-level and only dropped by a write made in this tab, so an image
   * uploaded anywhere else — another tab, another person, an import — is missing from it until
   * a reload. Resolving the current choice through it alone made the editor show "Choose
   * image" and no thumbnail for a picture the list beside it was drawing perfectly, because
   * the list reads the record and this read a stale list.
   *
   * The record travels with the url, the alt and the size, which is everything a thumbnail
   * needs, so it answers that question without the library having to be right.
   */
  current?: Nullable<IContentImageRef>;
  onPick: (imageId: Nullable<string>) => void;
  /** Narrows what is offered — headshots where a person goes. */
  kinds?: ContentImageKind[];
  disabled?: boolean;
  describedBy?: string;
}) {
  const { images, loading, error } = useLibrary();
  const [open, setOpen] = React.useState(false);
  // The image being cut. The picker closes for the crop and opens again if it is cancelled.
  const [cropping, setCropping] = React.useState<Nullable<IContentImage>>(null);
  // The library first, so a picture replaced on the Media screen is drawn as it is now; the
  // record second, so one the library has not heard of is still drawn.
  const chosen =
    images.find((image) => image.imageId === value)
    ?? (value !== null && current?.imageId === value ? current : null);
  const offered = placeable(images, kinds);

  return (
    <div className="grid gap-2">
      <Label>{label}</Label>
      <div className="flex items-center gap-3">
        <Thumb image={chosen} />
        <div className="grid justify-items-start gap-1">
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={disabled}
                aria-describedby={describedBy}
              >
                {chosen ? 'Change image' : 'Choose image'}
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-3xl">
              <DialogHeader>
                <DialogTitle>Choose an image</DialogTitle>
                <DialogDescription>
                  From the media library. Crop… places a cut of it and keeps the original.
                  Uploading is on the Media screen.
                </DialogDescription>
              </DialogHeader>
              <DialogBody className="max-h-[55dvh] grid-cols-[repeat(auto-fill,minmax(9rem,1fr))] gap-3 overflow-y-auto">
                {offered.map((image) => (
                  <OfferedImage
                    key={image.imageId}
                    image={image}
                    chosen={image.imageId === value}
                    sized
                    onPick={() => {
                      onPick(image.imageId);
                      setOpen(false);
                    }}
                    onCrop={() => {
                      setOpen(false);
                      setCropping(image);
                    }}
                  />
                ))}
                {offered.length === 0 && (
                  <p className="text-sm text-muted-foreground">
                    {loading
                      ? 'Reading the library…'
                      : 'The library has no image of this kind yet — upload one on the Media screen.'}
                  </p>
                )}
              </DialogBody>
            </DialogContent>
          </Dialog>
          {cropping && (
            <React.Suspense fallback={null}>
              <CropDialog
                image={cropping}
                onDone={(cropped) => {
                  setCropping(null);
                  if (cropped) onPick(cropped.imageId);
                  else setOpen(true);
                }}
              />
            </React.Suspense>
          )}
          {chosen && !disabled && (
            <Button type="button" variant="ghost" size="sm" onClick={() => onPick(null)}>
              Remove
            </Button>
          )}
          {/* A slot holding an id the library no longer offers: said rather than drawn empty,
              because an editor who cannot see the problem cannot fix it. */}
          {value !== null && chosen === null && !loading && (
            <p className="text-xs text-[var(--color-deep-magenta)]">
              {error ?? 'This image is no longer in the library.'}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
